import { useState, useEffect } from 'react'
import { completePayment } from '../../../../api-utils'
import { PAYMENT_CONSTANTS } from '../../../../payment-utils'

const {
  THANK_YOU_TITLE,
  RESERVATION_DETAILS_SUBTITLE
} = PAYMENT_CONSTANTS

/**
 * Hook to handle payment completion after Helcim payment succeeds:
 * 1. Updates page title/subtitle to show success
 * 2. Calls completePayment Netlify function to activate the lease
 * 3. Handles any errors during completion
 *
 * @param invoiceNumber - The Helcim invoice number
 * @param space - The space object (ensures space data is loaded)
 * @param paidDate - The date payment was completed
 * @param contractId - The eSignatures.io contract ID
 * @param paidCustomerId - The Helcim customer ID
 * @param transactionId - The Helcim transaction ID
 * @param payMethod - The payment method used
 * @param paidAmount - The amount paid
 * @returns Object with page title/subtitle and error
 */
export function usePaymentCompletion(
  invoiceNumber: string | null,
  space: any,
  paidDate: string | null,
  contractId: string | null,
  paidCustomerId: string | null,
  transactionId: string | null,
  payMethod: string | null,
  paidAmount: string | null
) {
  const [pageTitle, setPageTitle] = useState<string>()
  const [pageSubtitle, setPageSubtitle] = useState<string>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (invoiceNumber && space && paidDate && contractId) {
      setPageTitle(THANK_YOU_TITLE)
      setPageSubtitle(RESERVATION_DETAILS_SUBTITLE)

      // Complete payment using Netlify function
      completePayment({
        contractId: contractId,
        customerId: paidCustomerId || '',
        transactionId: transactionId || '',
        invoiceNumber: invoiceNumber,
        paymentMethod: payMethod || 'cc',
        amount: Number(paidAmount),
        dueDate: paidDate
      })
        .then((response: any) => {
          if (response.success) {
            console.log('Payment completed successfully:', response)
            setError(undefined)
            // Update Helcim customer & invoice IF NEEDED
          } else {
            console.error('Payment completion failed:', response.error)
            setError(`Payment completion failed: ${response.error || 'Unknown error'}`)
          }
        })
        .catch((err: any) => {
          console.error('Error completing payment:', err)
          setError(`Payment completion failed: ${err.message || 'Unknown error'}`)
        })
    }
  }, [invoiceNumber, space, paidDate, contractId, paidCustomerId, transactionId, payMethod, paidAmount])

  return { pageTitle, pageSubtitle, error }
}
